import { TranslateService } from '@ngx-translate/core';
import { ChatConversationService } from './../services/chat-conversation.service';
import { ChatAuthService } from './../services/chat-auth.service';
import { ChatCloudLinkService } from './../services/chat-cloudlink.service';
import { Message, Attachment } from '@mitel/cloudlink-sdk/chat';
import { MessageEx } from './messageex';
import { ContentTypes } from './constants';
import type { ChatConfig } from '../components/chat/chat.component';
import { defer } from 'rxjs';
import { retryWhen } from 'rxjs/operators';
import { SharedHelpers } from './sharedhelpers';
import { ChatLoggingService } from '../services/chat-logging.service';
import { HttpResponse } from '@mitel/cloudlink-sdk';
import { ChatCloudFileLink } from '../interfaces/chat-cloud-file-link';

export class MessageUtils {
  static errorString = 'errormsg::';

  static hasHtmlAnchorContent(text: string): boolean {
    return text ? /<a\s+[^>]*href/i.test(text) : false;
  }

  static stripHtmlAnchor(text: string): string {
    if (!text) return text;
    const match = text.match(/<a\s+[^>]*>(.*?)<\/a>/i);
    return match ? match[1] : text;
  }

  static ensureAnchorNewTab(text: string): string {
    if (!text) return text;
    return text.replace(/<a\b[^>]*>/gi, (anchorTag: string) => {
      const hasTarget = /\btarget\s*=/i.test(anchorTag);
      const hasRel = /\brel\s*=/i.test(anchorTag);
      const attributesToAdd = [!hasTarget ? ' target="_blank"' : '', !hasRel ? ' rel="noopener noreferrer"' : ''].join(
        ''
      );

      return attributesToAdd ? anchorTag.replace(/>$/, `${attributesToAdd}>`) : anchorTag;
    });
  }

  static getAttachmentContentType(message: Message, jsonMessagesAsSource: boolean = false) {
    if (this.isAttachment(message) || this.isAttachmentSystemMessage(message)) {
      let extIndex;
      let extn;
      if (this.isAttachment(message)) {
        extIndex = message.body.lastIndexOf('.');
        extn = message.body.substring(extIndex + 1).toLowerCase();
        // render as links when jsonMessagesAsSource
        if (this.hasHtmlAnchorContent(message.fallbackText) && jsonMessagesAsSource) {
          extn = 'octet-stream';
        }
      } else {
        let body = this.getAttachmentSystemMessageBody(message);
        extIndex = body.lastIndexOf('.');
        extn = body.substring(extIndex + 1).toLowerCase();
      }
      // parse file extension

      let type = 'application';

      switch (extn) {
        case 'gif':
        case 'jpeg':
        case 'jfif':
        case 'jpg':
        case 'webp':
        case 'png':
          type = 'image';
          break;
        case 'weba':
        case 'oga':
        case 'midi':
        case 'mp3':
        case 'ogg':
        case 'wav':
          type = 'audio';
          break;
        case '3gpp':
        case 'webm':
        case 'ogv':
        case 'mp4':
        case 'mov':
          type = 'video';
          break;
        case 'pdf':
          type = 'pdf';
          break;
        default:
          extn = 'octet-stream';
          break;
      }

      return `${type}/${extn}`;
    } else {
      return '';
    }
  }

  static isAttachment(message: Message): boolean {
    return message?.contentType === ContentTypes.ATTACHMENT;
  }

  static isVoiceNote(message: MessageEx): boolean {
    return this.isAttachment(message) && message.clCompChat_attachmentContentType === 'audio/webm';
  }

  static isLocation(message: MessageEx): boolean {
    let retVal: boolean = false;

    if (message?.contentType === ContentTypes.LOCATION) {
      retVal = true;
    } else {
      let gmaps = /.*google.*maps.*@/g;
      if (message.body.match(gmaps)) {
        retVal = true;
      }

      let bing = /.*bing.*maps.*cp=/g;
      if (message.body.match(bing)) {
        retVal = true;
      }
    }

    return retVal;
  }

  static isActionCard(message: MessageEx): boolean {
    return message?.contentType === ContentTypes.ACTIONCARD;
  }

  // The link a CLOUDFILELINK message shares, or null when its body isn't one - any sender can post this content type,
  // so only an https link with a name is taken
  static getCloudFileLink(message: Message): ChatCloudFileLink | null {
    if (message?.contentType !== ContentTypes.CLOUDFILELINK) {
      return null;
    }
    try {
      const link = JSON.parse(message.body) as ChatCloudFileLink;
      if (typeof link?.name !== 'string' || !link.name || typeof link.webUrl !== 'string') {
        return null;
      }
      if (new URL(link.webUrl).protocol !== 'https:') {
        return null;
      }
      return { ...link, size: typeof link.size === 'number' && link.size >= 0 ? link.size : undefined };
    } catch {
      return null;
    }
  }

  static isMissedCall(message: MessageEx): boolean {
    return message?.contentType === ContentTypes.MISSEDCALL;
  }

  static isVoicemail(message: MessageEx): boolean {
    return message?.contentType === ContentTypes.VOICEMAIL;
  }

  static isQuoteMessage(message: MessageEx): boolean {
    let retVal: boolean = false;
    retVal = message?.contentType.indexOf(ContentTypes.QUOTED) >= 0;
    return retVal;
  }

  static isWhisperMessage(message: MessageEx): boolean {
    return message?.contentType === ContentTypes.WHISPER;
  }

  static isWhisperUserMessage(message: MessageEx): boolean {
    return message?.contentType === ContentTypes.WHISPER && message.body.indexOf(MessageUtils.errorString) === -1;
  }

  static isWhisperError(message: MessageEx): boolean {
    return message?.contentType === ContentTypes.WHISPER && message.body.indexOf(MessageUtils.errorString) > -1;
  }

  static isSystemMessage(message: MessageEx): boolean {
    return message?.contentType === ContentTypes.SYSTEMMESSAGE;
  }

  static isAttachmentSystemMessage(message: MessageEx): boolean {
    try {
      if (message?.contentType === ContentTypes.ATTACHMENTSYSTEMMESSAGE) {
        return true;
      } else if (message?.contentType !== ContentTypes.SYSTEMMESSAGE) {
        return false;
      }
      let systemMessage = JSON.parse(message.body);
      if (systemMessage?.lookupCode === 'attachment-add' && message?.createdBy === 'system') {
        message.contentType = ContentTypes.ATTACHMENTSYSTEMMESSAGE;
        this.updateAttachmentSystemMessageCreatedBy(message);
        return true;
      }
    } catch (ex) {}
    return false;
  }

  static getAttachmentSystemMessageBody(message: MessageEx): string {
    try {
      let systemMessage = JSON.parse(message.body);
      if (systemMessage?.data?.length === 2) {
        return systemMessage.data[1];
      }
    } catch (ex) {}
    return '';
  }

  static updateAttachmentSystemMessageCreatedBy(message: MessageEx): string {
    try {
      let systemMessage = JSON.parse(message.body);
      if (systemMessage?.data?.length === 2) {
        message.createdBy = systemMessage.data[0].replace('@', '');
      }
    } catch (ex) {}
    return '';
  }

  static isErrorMessage(message: MessageEx): boolean {
    return message?.contentType === ContentTypes.ERROR;
  }

  static isFailedMessage(message: MessageEx): boolean {
    return message.clCompChat_state === 'failed';
  }

  static isPendingMessage(message: MessageEx): boolean {
    return message.clCompChat_state === 'pending';
  }

  static getAttachmentDisplayName(attachment: Attachment, stripHtmlAnchor: boolean = false): string {
    var attachmentName = attachment.name;
    if (attachment.displayName) {
      attachmentName = MessageUtils.hasHtmlAnchorContent(attachment.displayName)
        ? stripHtmlAnchor
          ? MessageUtils.stripHtmlAnchor(attachment.displayName)
          : MessageUtils.ensureAnchorNewTab(attachment.displayName)
        : attachment.displayName;
    } else if (attachment.description) {
      if (attachment.name.lastIndexOf('.') > 0 && attachment.description.lastIndexOf('.') > 0) {
        if (
          attachment.name.substring(attachment.name.lastIndexOf('.'), attachment.name.length) ===
          attachment.description.substring(attachment.description.lastIndexOf('.'), attachment.description.length)
        ) {
          //For backwards compatibility
          attachmentName = attachment.description;
        }
      }
    }
    return attachmentName;
  }

  static checkAllowedPlatformSystemMessage(message: Message, config: ChatConfig) {
    if (message?.contentType === ContentTypes.SYSTEMMESSAGE && message?.createdBy === 'system') {
      try {
        let systemMessage = JSON.parse(message.body);
        //Always allow attachment add system messages
        if (systemMessage?.lookupCode === 'attachment-add') {
          return true;
        }
        //Never show attachment delete system messages
        if (systemMessage?.lookupCode === 'attachment-delete') {
          return false;
        }
        if (systemMessage?.lookupCode && config?.allowedPlatformSystemMessages) {
          if (config.allowedPlatformSystemMessages.indexOf(systemMessage.lookupCode) === -1) {
            return false;
          }
        }
      } catch {}
    }
    return true;
  }

  static async checkAndDeleteAttachmentMessage(
    filename: string,
    conversationId: string,
    chatCloudlinkService: ChatCloudLinkService,
    chatAuthService: ChatAuthService,
    chatLoggingService: ChatLoggingService,
    chatConversationService: ChatConversationService,
    translate: TranslateService
  ) {
    // remove $search and add body eq file.name when the following issue is fixed https://github.com/mitel-networks/platform-api-conversations/issues/1512
    let fileName = filename;
    let dotIndex = filename.indexOf('.');
    if (dotIndex > -1) {
      fileName = filename.substring(0, dotIndex);
    }

    let res: HttpResponse<any> = null;
    try {
      res = await defer(() =>
        chatCloudlinkService.chat.getMessages({
          conversationId: conversationId,
          filter: `createdBy eq '${chatAuthService.userId}' and conversationId eq '${conversationId}' and contentType eq '${ContentTypes.ATTACHMENT}'`,
          search: fileName
        })
      )
        .pipe(
          retryWhen(
            SharedHelpers.genericRetryStrategy(
              '[messageutils.checkAndDeleteAttachmentMessage()] - get messages ' + conversationId,
              chatLoggingService
            )
          )
        )
        .toPromise();
    } catch (ex) {
      chatLoggingService.error({
        logMessage: 'messageutils.checkAndDeleteAttachmentMessage() - Error searching for attachment message',
        logParameters: [conversationId, fileName, ex]
      });
    }

    let attachmentMessages = res.body;
    if (attachmentMessages?._embedded?.items?.length) {
      try {
        await defer(() =>
          chatConversationService.deleteMessage({
            conversationId: conversationId,
            messageId: attachmentMessages._embedded.items[0].messageId
          })
        )
          .pipe(
            retryWhen(
              SharedHelpers.genericRetryStrategy(
                '[messageutils.checkAndDeleteAttachmentMessage()] - delete message ' +
                  conversationId +
                  ' ' +
                  attachmentMessages._embedded.items[0].messageId,
                chatLoggingService
              )
            )
          )
          .toPromise();
        chatLoggingService.info({
          logMessage: 'messageutils.checkAndDeleteAttachmentMessage() - Deleted attachment message',
          logParameters: [conversationId, attachmentMessages._embedded.items[0].messageId]
        });
      } catch (ex) {
        chatConversationService.errorDialog$.next({
          exceptionLogEvent: {
            logMessage: 'messageutils.checkAndDeleteAttachmentMessage(): Error deleting message',
            logParameters: [attachmentMessages._embedded.items[0].messageId, ex]
          },
          errorMessage: translate.instant('cl_chat.errorDeletingMessage')
        });
      }
    } else {
      chatLoggingService.info({
        logMessage: 'messageutils.checkAndDeleteAttachmentMessage() - No attachment message found',
        logParameters: [conversationId, filename]
      });
    }
  }

  static async getMessageIdOfAttachmentMessage(
    filename: string,
    conversationId: string,
    chatCloudlinkService: ChatCloudLinkService,
    chatLoggingService: ChatLoggingService
  ): Promise<string> {
    // remove $search and add body eq file.name when the following issue is fixed https://github.com/mitel-networks/platform-api-conversations/issues/1512
    let fileName = filename;
    let dotIndex = filename.indexOf('.');
    if (dotIndex > -1) {
      fileName = filename.substring(0, dotIndex);
    }

    let res: HttpResponse<any> = null;
    try {
      res = await defer(() =>
        chatCloudlinkService.chat.getMessages({
          conversationId: conversationId,
          filter: `conversationId eq '${conversationId}' and contentType eq '${ContentTypes.ATTACHMENT}'`,
          search: fileName
        })
      )
        .pipe(
          retryWhen(
            SharedHelpers.genericRetryStrategy(
              '[messageutils.getMessageIdOfAttachmentMessage()] - get messages ' + conversationId,
              chatLoggingService
            )
          )
        )
        .toPromise();
    } catch (ex) {
      chatLoggingService.error({
        logMessage: 'messageutils.getMessageIdOfAttachmentMessage() - Error searching for attachment message',
        logParameters: [conversationId, fileName, ex]
      });
    }

    let attachmentMessages = res.body;

    if (attachmentMessages?._embedded?.items?.length) {
      return attachmentMessages._embedded.items[0].messageId;
    } else {
      chatLoggingService.info({
        logMessage: 'messageutils.getMessageIdOfAttachmentMessage() - No attachment message found',
        logParameters: [conversationId, filename]
      });
      return null;
    }
  }

  static removeTrailingNewLine(msg: string) {
    if (msg && typeof msg === 'string' && msg.length > 0) {
      msg = msg.trimEnd();
    }
    if (!msg.endsWith('\n\n')) {
      return msg.replace(/\n$/, '');
    }
    return msg;
  }

  static async deleteAttachment(
    item: MessageEx,
    chatLoggingService: ChatLoggingService,
    chatConversationService: ChatConversationService,
    translate: TranslateService
  ) {
    let attachmentId = item.body;
    if (this.isAttachmentSystemMessage(item)) {
      attachmentId = this.getAttachmentSystemMessageBody(item);
    }

    try {
      chatLoggingService.info({
        logMessage: 'messageutils.deleteAttachment() - Deleting attachment',
        logParameters: [item.conversationId, attachmentId]
      });
      await defer(() =>
        chatConversationService.deleteAttachment({
          conversationId: item.conversationId,
          attachmentId
        })
      )
        .pipe(
          retryWhen(
            SharedHelpers.genericRetryStrategy(
              'messageutils.deleteAttachment(): delete attachment ' + item.conversationId + ' ' + item.body,
              chatLoggingService
            )
          )
        )
        .toPromise();
    } catch (ex) {
      //404 is ok, means it doesn't exist
      if (ex.statusCode !== 404) {
        chatLoggingService.messageError({
          exceptionLogEvent: {
            logMessage: 'messageutils.deleteAttachment(): Error deleting attachment',
            logParameters: [ex]
          },
          errorMessage: translate.instant('cl_chat.errorDeletingAttachment')
        });
      } else {
        chatLoggingService.info({
          logMessage: 'messageutils.deleteAttachment() No attachment exists for message',
          logParameters: [item.conversationId, item.messageId]
        });
      }
    }
  }
}
